import PageShell from "../components/PageShell";
import {
  Trophy,
  Flame,
  CalendarDays,
  Users,
  ArrowRight,
  Sparkles,
  Smartphone,
  Repeat,
  Target,
  TrendingUp,
  Medal,
  Zap,
  Gamepad2,
  HeartPulse,
  CheckCircle2,
} from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";

const INSTAGRAM_URL =
  "https://www.instagram.com/irlstats?igsh=MXJ4ajI1YTR0Ynh2OA%3D%3D&utm_source=qr";

const ASSETS = {
  home: "https://customer-assets.emergentagent.com/job_irl-stats-launch/artifacts/xitkp8eu_home%20page.png",
  coop: "https://customer-assets.emergentagent.com/job_irl-stats-launch/artifacts/nj6uinmv_co-op.png",
  mastery:
    "https://customer-assets.emergentagent.com/job_irl-stats-launch/artifacts/namk9lny_masteries_exact_1284x2778.jpg",
  pr: "https://customer-assets.emergentagent.com/job_irl-stats-launch/artifacts/pqvkoo70_pr%20progression.png",
  achievements:
    "https://customer-assets.emergentagent.com/job_irl-stats-launch/artifacts/pivpe7a2_achievements.png",
  calendar:
    "https://customer-assets.emergentagent.com/job_irl-stats-launch/artifacts/rm4amxq5_calendar.png",
  progress:
    "https://customer-assets.emergentagent.com/job_irl-stats-launch/artifacts/94dhznn3_progresss.png",
};

/* ── Reusable section header ─────────────────────────────────────────── */
const Eyebrow = ({ children }) => (
  <p className="text-amber-300/80 text-xs tracking-[0.25em] uppercase mb-4">
    {children}
  </p>
);

const SectionHeading = ({ children, testId }) => (
  <h2
    data-testid={testId}
    className="font-cinzel text-3xl sm:text-4xl lg:text-5xl text-amber-50 leading-[1.1] uppercase tracking-tight"
  >
    {children}
  </h2>
);

/* ── 1. HERO ─────────────────────────────────────────────────────────── */
const Hero = () => (
  <section data-testid="home-hero" className="relative overflow-hidden">
    <div className="absolute inset-0 pointer-events-none">
      <div className="absolute -top-32 -left-32 h-[28rem] w-[28rem] rounded-full bg-amber-500/10 blur-3xl" />
      <div className="absolute bottom-0 right-0 h-[28rem] w-[28rem] rounded-full bg-amber-600/[0.06] blur-3xl" />
    </div>

    <div className="relative max-w-6xl mx-auto px-5 sm:px-8 pt-20 pb-24 md:pt-28 md:pb-32">
      <div className="grid md:grid-cols-2 gap-12 items-center">
        <div className="space-y-7">
          <div className="flex items-center gap-4">
            <img
              src={ASSETS.home}
              alt="IRL Stats logo mark"
              className="hidden sm:block h-14 w-14 rounded-xl shadow-[0_0_30px_-8px_rgba(251,191,36,0.55)] object-cover object-top"
            />
            <div
              data-testid="hero-badge"
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-amber-400/30 bg-amber-400/5 text-amber-300 text-xs tracking-[0.18em] uppercase"
            >
              <Sparkles size={12} />
              Now leveling up
            </div>
          </div>

          <h1
            data-testid="hero-headline"
            className="font-cinzel text-4xl sm:text-5xl lg:text-6xl text-amber-50 leading-[1.02] uppercase tracking-tight"
          >
            Stop skipping the gym.
            <span className="block text-amber-300 mt-2">
              Level up your real life.
            </span>
          </h1>

          <p
            data-testid="hero-subheadline"
            className="text-stone-300 text-base sm:text-lg leading-relaxed max-w-lg"
          >
            Gamified fitness tracking, real progression, and co-op
            accountability that keeps you showing up.
          </p>

          <p className="text-stone-500 text-sm">
            Built for lifters, gamers, and competitive humans.
          </p>

          <div className="flex flex-wrap gap-4 pt-2">
            <button
              data-testid="hero-cta-coming-soon"
              disabled
              className="inline-flex items-center gap-3 px-6 py-3.5 rounded-md bg-gradient-to-b from-amber-300 to-amber-500 text-black font-semibold tracking-wide cursor-not-allowed shadow-[0_0_25px_rgba(251,191,36,0.25)]"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                <path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
              </svg>
              Coming Soon on the App Store
            </button>
            <a
              href="#coop"
              data-testid="hero-cta-explore-coop"
              className="inline-flex items-center gap-2 px-6 py-3.5 rounded-md border border-amber-400/30 text-amber-200 hover:bg-amber-400/5 hover:border-amber-400/60 transition-colors tracking-wide"
            >
              Explore Co-op Mode
              <ArrowRight size={16} />
            </a>
          </div>
        </div>

        {/* HERO PHONE — existing dashboard screenshot */}
        <div className="relative">
          <div className="pointer-events-none absolute -inset-6 rounded-3xl bg-amber-400/10 blur-3xl" />
          <img
            src={ASSETS.home}
            alt="IRL Stats home screen — XP, levels, streaks, and skills"
            data-testid="hero-app-screenshot"
            loading="eager"
            className="relative mx-auto max-w-sm w-full rounded-2xl shadow-[0_0_60px_-10px_rgba(251,191,36,0.5)]"
          />
        </div>
      </div>
    </div>
  </section>
);

/* ── 2. ACCOUNTABILITY / SOCIAL PROOF ───────────────────────────────── */
const Accountability = () => {
  const stats = [
    {
      icon: TrendingUp,
      value: "Higher",
      label: "Accountability",
      desc: "Working out with a partner consistently outperforms going it alone.",
    },
    {
      icon: Users,
      value: "2",
      label: "Players · One Device",
      desc: "Train together on the same phone. No setup, no second account.",
    },
    {
      icon: Zap,
      value: "Real-time",
      label: "Co-op Logging",
      desc: "Switch between lifters between sets and keep momentum.",
    },
  ];

  return (
    <section
      data-testid="home-accountability"
      className="relative border-t border-amber-500/10 bg-black"
    >
      <div className="max-w-6xl mx-auto px-5 sm:px-8 py-20 md:py-24">
        <div className="max-w-3xl">
          <Eyebrow>── The Real Problem</Eyebrow>
          <SectionHeading testId="accountability-heading">
            Most people don't fail from{" "}
            <span className="text-amber-300">bad programming.</span>
            <span className="block">They fail from inconsistency.</span>
          </SectionHeading>
          <p className="text-stone-300 text-base sm:text-lg mt-6 leading-relaxed max-w-2xl">
            Social accountability has been shown to improve exercise adherence
            and consistency. IRL Stats is built around that simple truth.
          </p>
          <p className="text-stone-500 text-xs mt-3">
            Sources: Community Preventive Services Task Force · Nature
            Communications
          </p>
        </div>

        <div className="grid sm:grid-cols-3 gap-5 mt-12">
          {stats.map((s, idx) => (
            <div
              key={s.label}
              data-testid={`accountability-stat-${idx}`}
              className="relative rounded-xl border border-amber-500/15 bg-stone-950/60 p-6 hover:border-amber-400/40 transition-colors"
            >
              <div className="h-10 w-10 rounded-lg border border-amber-400/30 bg-amber-400/5 flex items-center justify-center mb-5">
                <s.icon className="text-amber-300" size={18} />
              </div>
              <p className="font-cinzel text-amber-100 text-3xl leading-none">
                {s.value}
              </p>
              <p className="text-amber-300/80 text-xs tracking-[0.18em] uppercase mt-2">
                {s.label}
              </p>
              <p className="text-stone-400 text-sm mt-3 leading-relaxed">
                {s.desc}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ── 3. CO-OP FLAGSHIP ───────────────────────────────────────────────── */
const CoopFlagship = () => {
  const bullets = [
    { icon: Smartphone, text: "Shared device gym partner mode" },
    { icon: Repeat, text: "Switch between Player 1 and Player 2" },
    { icon: Zap, text: "Separate XP for each lifter" },
    { icon: Trophy, text: "Separate PRs and milestones" },
    { icon: TrendingUp, text: "Separate progression history" },
    { icon: Users, text: "Built-in accountability" },
  ];

  return (
    <section
      id="coop"
      data-testid="home-coop"
      className="relative border-t border-amber-500/10 bg-gradient-to-b from-stone-950 to-black overflow-hidden"
    >
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 h-[700px] w-[700px] rounded-full bg-amber-500/[0.07] blur-3xl" />
      </div>

      <div className="relative max-w-6xl mx-auto px-5 sm:px-8 py-24 md:py-32">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <div className="order-2 lg:order-1">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-amber-400/30 bg-amber-400/5 text-amber-300 text-xs tracking-[0.2em] uppercase mb-6">
              <Users size={12} />
              Flagship · Local Co-op
            </div>
            <SectionHeading testId="coop-heading">
              Train together.
              <span className="block text-amber-300 mt-2">
                One device. Two lifters.
              </span>
            </SectionHeading>
            <p className="text-stone-300 text-base sm:text-lg mt-6 leading-relaxed">
              Same bench. Same workout.{" "}
              <span className="text-amber-200">Different stats.</span>{" "}
              <span className="text-amber-200">Different PRs.</span>{" "}
              <span className="text-amber-200">Separate progression.</span>
            </p>

            <ul className="mt-8 grid sm:grid-cols-2 gap-3">
              {bullets.map((b) => (
                <li
                  key={b.text}
                  className="flex items-start gap-3 rounded-lg border border-amber-500/10 bg-stone-950/40 px-3 py-3"
                >
                  <div className="shrink-0 h-7 w-7 rounded-md border border-amber-400/25 bg-amber-400/5 flex items-center justify-center mt-0.5">
                    <b.icon className="text-amber-300" size={13} />
                  </div>
                  <span className="text-stone-200 text-sm leading-snug">
                    {b.text}
                  </span>
                </li>
              ))}
            </ul>

            <div className="mt-8 p-4 rounded-lg border border-amber-500/25 bg-amber-400/[0.05]">
              <p className="text-stone-300 text-sm">
                <span className="text-amber-300 font-medium">Built for IRL training.</span>{" "}
                Track separate progress while you lift together — same bar,
                same set, different stats.
              </p>
            </div>
          </div>

          <div className="order-1 lg:order-2 relative">
            <div className="pointer-events-none absolute -inset-8 rounded-3xl bg-amber-400/15 blur-3xl" />
            <img
              src={ASSETS.coop}
              alt="IRL Stats Co-op Mode — two lifters tracking on one device"
              data-testid="coop-image"
              loading="lazy"
              className="relative w-full rounded-2xl border border-amber-500/15 shadow-[0_0_70px_-10px_rgba(251,191,36,0.55)]"
            />
          </div>
        </div>
      </div>
    </section>
  );
};

/* ── 4. GAMIFICATION ─────────────────────────────────────────────────── */
const Gamification = () => {
  const features = [
    {
      icon: TrendingUp,
      title: "XP Progression",
      desc: "Every set, rep, and workout earns experience. Watch your level climb in real time.",
    },
    {
      icon: Gamepad2,
      title: "Levels",
      desc: "Hunter levels, skill levels, muscle levels. Real progression you can actually see.",
    },
    {
      icon: Flame,
      title: "Streaks",
      desc: "Daily streaks keep the fire alive. Miss a day, lose the streak — simple as that.",
    },
    {
      icon: Medal,
      title: "Achievements",
      desc: "64+ badges across consistency, volume, PRs, and beast-mode moments.",
    },
    {
      icon: CheckCircle2,
      title: "Daily Tracking",
      desc: "A daily flow that tells you exactly what's next and what's already locked in.",
    },
  ];

  return (
    <section
      data-testid="home-gamification"
      className="relative border-t border-amber-500/10 bg-black"
    >
      <div className="max-w-6xl mx-auto px-5 sm:px-8 py-20 md:py-28">
        <div className="grid lg:grid-cols-[1fr_auto] gap-12 items-start">
          <div className="max-w-2xl">
            <Eyebrow>── Dopamine Engine</Eyebrow>
            <SectionHeading testId="gamification-heading">
              Fitness that{" "}
              <span className="text-amber-300 italic">feels like a game.</span>
            </SectionHeading>
            <p className="text-stone-300 text-base sm:text-lg mt-6 leading-relaxed">
              Every rep matters. Every workout levels you up.
            </p>
          </div>

          <img
            src={ASSETS.home}
            alt="IRL Stats dashboard"
            loading="lazy"
            className="hidden lg:block w-44 rounded-xl shadow-[0_0_40px_-10px_rgba(251,191,36,0.4)] opacity-90"
          />
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-12">
          {features.map((f, idx) => (
            <div
              key={f.title}
              data-testid={`gamification-card-${idx}`}
              className="group relative rounded-xl border border-amber-500/15 bg-stone-950/60 p-6 hover:border-amber-400/50 hover:bg-stone-900/60 transition-all duration-300"
            >
              <div className="h-11 w-11 rounded-lg border border-amber-400/30 bg-amber-400/5 flex items-center justify-center mb-5 group-hover:border-amber-300/70 group-hover:bg-amber-400/10 transition-colors">
                <f.icon className="text-amber-300" size={20} />
              </div>
              <h3 className="font-cinzel text-amber-100 text-lg tracking-wide mb-2">
                {f.title}
              </h3>
              <p className="text-stone-400 text-sm leading-relaxed">{f.desc}</p>
              <div className="absolute top-4 right-4 text-amber-500/30 text-[10px] tracking-[0.2em] uppercase">
                0{idx + 1}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ── 5. MUSCLE MASTERY (phone LEFT / text RIGHT) ─────────────────────── */
const MuscleMastery = () => {
  const bullets = [
    "Individual muscle leveling",
    "Dominant vs lagging muscles",
    "Visual progression",
    "Smarter, more balanced training",
  ];

  return (
    <section
      data-testid="home-mastery"
      className="relative border-t border-amber-500/10 bg-gradient-to-b from-black to-stone-950"
    >
      <div className="max-w-6xl mx-auto px-5 sm:px-8 py-20 md:py-28">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <div className="relative order-2 lg:order-1">
            <div className="pointer-events-none absolute -inset-6 rounded-3xl bg-amber-400/10 blur-3xl" />
            <img
              src={ASSETS.mastery}
              alt="Muscle Mastery — per-muscle progression levels"
              data-testid="mastery-image"
              loading="lazy"
              className="relative w-full max-w-md mx-auto rounded-2xl border border-amber-500/15 shadow-[0_0_50px_-10px_rgba(251,191,36,0.4)]"
            />
          </div>

          <div className="order-1 lg:order-2">
            <Eyebrow>── Muscle Mastery</Eyebrow>
            <SectionHeading testId="mastery-heading">
              Master every muscle.
            </SectionHeading>
            <p className="text-stone-300 text-base sm:text-lg mt-6 leading-relaxed">
              Track progression by individual muscle groups, spot weak points,
              and see exactly what's dominating.
            </p>
            <ul className="mt-7 space-y-3">
              {bullets.map((b) => (
                <li key={b} className="flex items-start gap-3">
                  <CheckCircle2
                    className="text-amber-300 shrink-0 mt-0.5"
                    size={18}
                  />
                  <span className="text-stone-200 text-base">{b}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
};

/* ── 6. PR PROGRESSION (text LEFT / phone RIGHT) ─────────────────────── */
const PRProgression = () => {
  const bullets = [
    "Smart progression guidance",
    "Percentage-based intensity suggestions",
    "Goal tracking toward your next PR",
    "PR milestone detection",
  ];

  return (
    <section
      data-testid="home-pr"
      className="relative border-t border-amber-500/10 bg-black"
    >
      <div className="max-w-6xl mx-auto px-5 sm:px-8 py-20 md:py-28">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <div>
            <Eyebrow>── PR Progression</Eyebrow>
            <SectionHeading testId="pr-heading">
              Chase your next PR{" "}
              <span className="text-amber-300 italic">automatically.</span>
            </SectionHeading>
            <p className="text-stone-300 text-base sm:text-lg mt-6 leading-relaxed">
              Know exactly what weight, reps, and intensity move you toward
              your next milestone.
            </p>
            <ul className="mt-7 space-y-3">
              {bullets.map((b) => (
                <li key={b} className="flex items-start gap-3">
                  <Target
                    className="text-amber-300 shrink-0 mt-0.5"
                    size={18}
                  />
                  <span className="text-stone-200 text-base">{b}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="relative">
            <div className="pointer-events-none absolute -inset-6 rounded-3xl bg-amber-400/10 blur-3xl" />
            <img
              src={ASSETS.pr}
              alt="PR Progression — Barbell Bench Press with weight, reps, and intensity"
              data-testid="pr-image"
              loading="lazy"
              className="relative w-full max-w-md mx-auto rounded-2xl border border-amber-500/15 shadow-[0_0_50px_-10px_rgba(251,191,36,0.4)]"
            />
          </div>
        </div>
      </div>
    </section>
  );
};

/* ── 7. ACHIEVEMENTS ─────────────────────────────────────────────────── */
const Achievements = () => {
  const categories = [
    { title: "Consistency", desc: "Streaks, show-ups, lock-ins." },
    { title: "Milestones", desc: "First sets, first PRs, first workouts." },
    { title: "PR Hunters", desc: "Crush new personal records." },
    { title: "Volume Beasts", desc: "100K Club, lifetime volume goals." },
  ];

  return (
    <section
      data-testid="home-achievements"
      className="relative border-t border-amber-500/10 bg-gradient-to-b from-stone-950 to-black"
    >
      <div className="max-w-6xl mx-auto px-5 sm:px-8 py-20 md:py-28">
        <div className="grid lg:grid-cols-[1.1fr_1fr] gap-12 items-center">
          <div>
            <Eyebrow>── Achievements</Eyebrow>
            <SectionHeading testId="achievements-heading">
              Unlock achievements.
              <span className="block text-amber-300 mt-2">Stack wins.</span>
            </SectionHeading>
            <p className="text-stone-300 text-base sm:text-lg mt-6 leading-relaxed">
              Consistency, milestones, streaks, PRs, and volume achievements —
              all stacked, all visible, all yours.
            </p>

            <div className="grid sm:grid-cols-2 gap-3 mt-8">
              {categories.map((c, idx) => (
                <div
                  key={c.title}
                  data-testid={`achievement-card-${idx}`}
                  className="rounded-lg border border-amber-500/15 bg-stone-950/60 p-4 hover:border-amber-400/40 transition-colors"
                >
                  <div className="flex items-center gap-2">
                    <Medal className="text-amber-300" size={16} />
                    <p className="font-cinzel text-amber-100 text-sm tracking-wider uppercase">
                      {c.title}
                    </p>
                  </div>
                  <p className="text-stone-400 text-sm mt-2">{c.desc}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="relative">
            <div className="pointer-events-none absolute -inset-6 rounded-3xl bg-amber-400/10 blur-3xl" />
            <img
              src={ASSETS.achievements}
              alt="Achievements screen with unlocked badges"
              data-testid="achievements-image"
              loading="lazy"
              className="relative w-full max-w-md mx-auto rounded-2xl border border-amber-500/15 shadow-[0_0_50px_-10px_rgba(251,191,36,0.4)]"
            />
          </div>
        </div>
      </div>
    </section>
  );
};

/* ── 8. CALENDAR (phone RIGHT / text LEFT, smaller section) ──────────── */
const CalendarSection = () => (
  <section
    data-testid="home-calendar"
    className="relative border-t border-amber-500/10 bg-black"
  >
    <div className="max-w-6xl mx-auto px-5 sm:px-8 py-16 md:py-20">
      <div className="grid lg:grid-cols-2 gap-10 lg:gap-14 items-center">
        <div>
          <Eyebrow>── Planning</Eyebrow>
          <SectionHeading testId="calendar-heading">
            Plan your week.
            <span className="block text-amber-300 mt-2">Stay locked in.</span>
          </SectionHeading>
          <p className="text-stone-300 text-base mt-5 leading-relaxed">
            Schedule workouts, rest days, and keep streaks alive. Simple,
            visual, and built around how lifters actually plan their week.
          </p>
          <div className="flex gap-6 mt-6 text-sm">
            <span className="inline-flex items-center gap-2 text-stone-300">
              <CalendarDays size={14} className="text-amber-300" /> Week + Month views
            </span>
            <span className="inline-flex items-center gap-2 text-stone-300">
              <Flame size={14} className="text-amber-300" /> Streak tracking
            </span>
          </div>
        </div>

        <div className="relative">
          <div className="pointer-events-none absolute -inset-6 rounded-3xl bg-amber-400/8 blur-3xl" />
          <img
            src={ASSETS.calendar}
            alt="Calendar — weekly workout planner"
            data-testid="calendar-image"
            loading="lazy"
            className="relative w-full max-w-md mx-auto rounded-2xl border border-amber-500/15 shadow-[0_0_40px_-12px_rgba(251,191,36,0.35)]"
          />
        </div>
      </div>
    </div>
  </section>
);

/* ── 9. WHO THIS IS FOR ──────────────────────────────────────────────── */
const WhoFor = () => {
  const groups = [
    {
      icon: HeartPulse,
      title: "Casual Lifters",
      tag: "Accountability",
      desc: "You want to show up consistently and stop bailing on yourself.",
    },
    {
      icon: Trophy,
      title: "Competitive Lifters",
      tag: "Progression",
      desc: "You chase PRs, measure progress, and obsess over the numbers.",
    },
    {
      icon: Users,
      title: "Gym Duos",
      tag: "Co-op",
      desc: "You train with a partner and want each rep to count for both of you.",
    },
    {
      icon: Gamepad2,
      title: "Gamers",
      tag: "Dopamine",
      desc: "You're wired for XP, levels, and quests. So is your gym now.",
    },
  ];

  return (
    <section
      data-testid="home-who"
      className="relative border-t border-amber-500/10 bg-gradient-to-b from-black to-stone-950"
    >
      <div className="max-w-6xl mx-auto px-5 sm:px-8 py-20 md:py-24">
        <div className="max-w-2xl mb-12">
          <Eyebrow>── Who This Is For</Eyebrow>
          <SectionHeading testId="who-heading">
            Built for the people{" "}
            <span className="text-amber-300 italic">who don't quit.</span>
          </SectionHeading>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {groups.map((g, idx) => (
            <div
              key={g.title}
              data-testid={`who-card-${idx}`}
              className="group relative rounded-xl border border-amber-500/15 bg-stone-950/60 p-6 hover:border-amber-400/50 transition-colors"
            >
              <div className="h-11 w-11 rounded-lg border border-amber-400/30 bg-amber-400/5 flex items-center justify-center mb-5">
                <g.icon className="text-amber-300" size={20} />
              </div>
              <p className="text-amber-300/80 text-[10px] tracking-[0.2em] uppercase">
                → {g.tag}
              </p>
              <h3 className="font-cinzel text-amber-100 text-lg tracking-wide mt-2">
                {g.title}
              </h3>
              <p className="text-stone-400 text-sm mt-3 leading-relaxed">
                {g.desc}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ── 10. PRICING (informational only — no checkout) ──────────────────── */
const Pricing = () => {
  const free = [
    "Prebuilt workout templates",
    "Workout logging",
    "XP progression",
    "Achievements",
    "Calendar planning",
    "PR tracking",
    "2 custom templates",
    "Solo + co-op workout support",
  ];
  const pro = [
    "Unlimited custom templates",
    "Full custom workouts",
    "Advanced progression tools",
    "Premium analytics",
    "Future social features",
    "Premium achievements",
  ];

  return (
    <section
      id="pricing"
      data-testid="home-pricing"
      className="relative border-t border-amber-500/10 bg-black"
    >
      <div className="max-w-6xl mx-auto px-5 sm:px-8 py-20 md:py-28">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <Eyebrow>── Pricing</Eyebrow>
          <SectionHeading testId="pricing-heading">
            Start <span className="text-amber-300">free.</span>
          </SectionHeading>
          <p className="text-stone-400 text-base mt-5 leading-relaxed">
            Everything you need to start leveling up is in the free plan. Pro
            unlocks the deeper toolkit.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
          {/* FREE */}
          <div
            data-testid="pricing-card-free"
            className="relative rounded-2xl border border-amber-500/15 bg-stone-950/60 p-7"
          >
            <p className="text-stone-400 text-xs tracking-[0.2em] uppercase mb-3">
              Free
            </p>
            <div className="flex items-baseline gap-2">
              <span className="font-cinzel text-5xl text-amber-100">$0</span>
              <span className="text-stone-500 text-sm">/ forever</span>
            </div>
            <p className="text-stone-500 text-sm mt-2">
              No card, no catch. Start lifting today.
            </p>

            <ul className="mt-7 space-y-3 text-sm">
              {free.map((f) => (
                <li
                  key={f}
                  className="flex items-start gap-2.5 text-stone-300"
                >
                  <CheckCircle2
                    className="text-amber-300 mt-0.5 shrink-0"
                    size={16}
                  />
                  <span>{f}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* PRO */}
          <div
            data-testid="pricing-card-pro"
            className="relative rounded-2xl border border-amber-400/60 bg-gradient-to-b from-amber-400/[0.08] to-stone-950/40 p-7 shadow-[0_0_40px_-15px_rgba(251,191,36,0.4)]"
          >
            <div className="absolute -top-3 left-7 inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-gradient-to-b from-amber-300 to-amber-500 text-black text-[10px] font-semibold tracking-[0.15em] uppercase">
              <Sparkles size={10} />
              IRL Stats Pro
            </div>
            <p className="text-amber-200 text-xs tracking-[0.2em] uppercase mb-3">
              Pro
            </p>
            <div className="flex items-baseline gap-2">
              <span className="font-cinzel text-5xl text-amber-50">$2.99</span>
              <span className="text-stone-400 text-sm">/ month intro</span>
            </div>
            <p className="text-stone-400 text-sm mt-2">
              Everything in Free, plus the full progression toolkit.
            </p>

            <ul className="mt-7 space-y-3 text-sm">
              {pro.map((f) => (
                <li
                  key={f}
                  className="flex items-start gap-2.5 text-stone-200"
                >
                  <CheckCircle2
                    className="text-amber-300 mt-0.5 shrink-0"
                    size={16}
                  />
                  <span>{f}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <p className="text-stone-500 text-xs text-center mt-8">
          Subscriptions are managed through the Apple App Store. You can cancel
          anytime in your Apple ID settings.
        </p>
      </div>
    </section>
  );
};

/* ── 11. FAQ ─────────────────────────────────────────────────────────── */
const FAQ = () => {
  const items = [
    {
      q: "What makes IRL Stats different?",
      a: "Most workout apps just log sets and reps. IRL Stats turns training into progression you actually care about. Earn XP, level up muscle groups, unlock achievements, track PRs, and use co-op mode to train with a partner on the same device. It's built to make consistency addictive, not just measurable.",
    },
    {
      q: "Do I need a gym partner?",
      a: "Nope. IRL Stats is fully built for solo lifters too. Co-op is an optional feature for people who train with a friend, spouse, or gym partner and want added accountability.",
    },
    {
      q: "Can I use it solo?",
      a: "Absolutely. Solo mode is the default experience. Track workouts, build templates, chase PRs, level muscles, earn achievements, and monitor progress exactly like a normal premium workout app — with way more dopamine.",
    },
    {
      q: "Is there a free version?",
      a: "Yes. IRL Stats is free to start. Free includes workout logging, XP progression, achievements, PR tracking, calendar planning, prebuilt workout templates, 2 custom workout templates, and solo + co-op workout support. Pro unlocks advanced features like unlimited templates, deeper progression tools, premium analytics, and future social upgrades.",
    },
    {
      q: "How does co-op work?",
      a: "Co-op lets two lifters train together using one phone. Each person keeps their own weights, reps, PRs, XP, muscle progression, and achievements. Just swap between Player 1 and Player 2 between sets — same workout, separate stats.",
    },
    {
      q: "Will Android be supported?",
      a: "Yes — Android support is planned. IRL Stats is launching on iOS first so we can polish the experience, then expand to Android as quickly as possible.",
    },
  ];

  return (
    <section
      id="faq"
      data-testid="home-faq"
      className="relative border-t border-amber-500/10 bg-gradient-to-b from-stone-950 to-black"
    >
      <div className="max-w-3xl mx-auto px-5 sm:px-8 py-20 md:py-24">
        <div className="text-center mb-12">
          <Eyebrow>── FAQ</Eyebrow>
          <SectionHeading testId="faq-heading">
            Got <span className="text-amber-300">questions?</span>
          </SectionHeading>
        </div>

        <Accordion
          type="single"
          collapsible
          className="w-full"
          data-testid="faq-accordion"
        >
          {items.map((it, idx) => (
            <AccordionItem
              key={it.q}
              value={`item-${idx}`}
              data-testid={`faq-item-${idx}`}
              className="border-amber-500/15 border-b last:border-b-0"
            >
              <AccordionTrigger className="text-left text-amber-100 font-cinzel text-base sm:text-lg tracking-wide hover:no-underline py-5 [&[data-state=open]]:text-amber-300">
                {it.q}
              </AccordionTrigger>
              <AccordionContent className="text-stone-300 text-[15px] leading-relaxed pb-5 pr-6">
                {it.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </section>
  );
};

/* ── 12. FINAL CTA ───────────────────────────────────────────────────── */
const FinalCTA = () => (
  <section
    data-testid="home-final-cta"
    className="relative border-t border-amber-500/10 bg-black overflow-hidden"
  >
    <div className="absolute inset-0 pointer-events-none">
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 h-[500px] w-[500px] rounded-full bg-amber-500/10 blur-3xl" />
    </div>
    <div className="relative max-w-4xl mx-auto px-5 sm:px-8 py-24 md:py-32 text-center">
      <h2
        data-testid="final-cta-heading"
        className="font-cinzel text-3xl sm:text-4xl lg:text-5xl text-amber-50 leading-[1.1] uppercase tracking-tight"
      >
        The grind is real.
        <span className="block text-amber-300 mt-2">
          So should your progression be.
        </span>
      </h2>
      <p className="text-stone-400 mt-6 max-w-xl mx-auto">
        Follow along for launch updates, behind-the-scenes builds, and PR-day
        chaos.
      </p>
      <div className="mt-9 flex flex-wrap gap-4 justify-center">
        <a
          href={INSTAGRAM_URL}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="final-cta-instagram"
          className="inline-flex items-center gap-3 px-7 py-3.5 rounded-md bg-gradient-to-b from-amber-300 to-amber-500 text-black font-semibold tracking-wide shadow-[0_0_25px_rgba(251,191,36,0.25)] hover:shadow-[0_0_45px_rgba(251,191,36,0.45)] transition-shadow"
        >
          Follow @irlstats
          <ArrowRight size={16} />
        </a>
      </div>
    </div>
  </section>
);

/* ── PAGE ────────────────────────────────────────────────────────────── */
const Home = () => (
  <PageShell testId="home-page">
    <Hero />
    <Accountability />
    <CoopFlagship />
    <Gamification />
    <MuscleMastery />
    <PRProgression />
    <Achievements />
    <CalendarSection />
    <WhoFor />
    <Pricing />
    <FAQ />
    <FinalCTA />
  </PageShell>
);

export default Home;
